import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, Fragment as _Fragment, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "root"
}
const _hoisted_2 = {
  key: 2,
  class: "root"
}
const _hoisted_3 = { class: "epinios-table" }
const _hoisted_4 = { style: {"justify-content":"space-between","align-items":"center","margin-bottom":"1rem"} }
const _hoisted_5 = { class: "epinio-row" }
const _hoisted_6 = { class: "epinio-row" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "epinio-row" }
const _hoisted_9 = { class: "epinio-row" }
const _hoisted_10 = ["onClick"]

import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';

import Loading from '@shell/components/Loading.vue';
import DataTable from '../components/tables/DataTable.vue';
import type { DataTableColumn } from '../components/tables/types';
import AsyncButton from '@shell/components/AsyncButton.vue';
import Link from '@shell/components/formatter/Link.vue';
import BadgeStateFormatter from '@shell/components/formatter/BadgeStateFormatter.vue';
import { MANAGEMENT } from '@shell/config/types';

import { EPINIO_MGMT_STORE, EPINIO_TYPES } from '../types';
import { _MERGE } from '@shell/plugins/dashboard-store/actions';
import EpinioCluster, { EpinioInfoPath } from '../models/epiniomgmt/epinio.io.management.cluster';
import epinioAuth, { EpinioAuthTypes } from '../utils/auth';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const store = useStore();
const t = store.getters['i18n/t'];

const clusters = ref<EpinioCluster[]>([]);
const currentCluster = ref<EpinioCluster | null>(null);
const allClusters = ref<any>(null);

const loading = ref(true);
const error = ref<Error | null>(null)

const displayClusters = computed(() => {
  const formattedRancherClusters = allClusters.value.filter((cluster: any) => {
    const epinioCluster = clusters.value.find((c: EpinioCluster) => c.id === cluster.id);
    return !epinioCluster;
  }).map((rancherCluster: any) => {
    const uninstalledCluster = new EpinioCluster({
      id:          rancherCluster.id,
      name:        rancherCluster.spec.displayName,
      namespace:   '',
      api:         null,
      loggedIn:    false,
      mgmtCluster: rancherCluster,
    }, { rootGetters: store.getters });

    // Manually set the state after construction
    uninstalledCluster.state = 'uninstalled';
    uninstalledCluster.metadata = {
      state: {
        transitioning: false,
        error:        false,
        message:      'Epinio not installed'
      }
    };

    return uninstalledCluster;
  })
  const mergedClusters = [...clusters.value, ...formattedRancherClusters];
  return mergedClusters;
})

onMounted(async () => {
  loading.value = true
  try {
    await store.dispatch('management/findAll', { type: MANAGEMENT.CLUSTER });
    allClusters.value = store.getters['management/all'](MANAGEMENT.CLUSTER);

    await store.dispatch(`${EPINIO_MGMT_STORE}/findAll`, { type: EPINIO_TYPES.CLUSTER }, { root: true })
    clusters.value = store.getters[`${EPINIO_MGMT_STORE}/all`](EPINIO_TYPES.CLUSTER)

    clusters.value.forEach((c: EpinioCluster) => testCluster(c))
  } catch (err) {
    error.value = err as Error
  } finally {
    loading.value = false
  }
})

const canRediscover = () => {
  return !displayClusters.value.find((c: EpinioCluster) => c.state === 'updating');
}

const rediscover = async (buttonCb: (success: boolean) => void)  => {
  await store.dispatch(
    `${ EPINIO_MGMT_STORE }/findAll`,
    { type: EPINIO_TYPES.CLUSTER, opt: { force: true, load: _MERGE } },
  );
  displayClusters.value.forEach((c: EpinioCluster) => testCluster(c));
  buttonCb(true);
}

const openInstall = (c: EpinioCluster) => {
  store.dispatch('cluster/promptModal', {
    component:      'InstallDialog',
    componentProps: {
      cluster:   c,
      onSuccess: () => {
        store.dispatch(
          `${ EPINIO_MGMT_STORE }/findAll`,
          { type: EPINIO_TYPES.CLUSTER, opt: { force: true, load: _MERGE } },
        ).then(() => {
          clusters.value = store.getters[`${ EPINIO_MGMT_STORE }/all`](EPINIO_TYPES.CLUSTER);
          clusters.value.forEach((cluster: EpinioCluster) => testCluster(cluster));
        });
      },
    },
  });
};

const login = async (c: EpinioCluster) =>{
  const isLoggedIn = await epinioAuth.isLoggedIn(c.createAuthConfig(EpinioAuthTypes.AGNOSTIC));

  if (isLoggedIn) {
    store.$router.push({
      name:   'epinio-c-cluster-dashboard',
      params: { cluster: c.id }
    });
  } else {
    currentCluster.value = c;
    store.dispatch('cluster/promptModal', {
      component: 'LoginDialog',
      componentProps: {
        cluster: currentCluster.value,
      },
    });
  }
}

const setClusterState = (
  cluster: EpinioCluster,
  state: string,
  metadataStateObj: {
    state: {
      transitioning: boolean,
      error: boolean,
      message: string
    }
  }) => {
  cluster['state'] = state;
  cluster['metadata'] = metadataStateObj;
}

const testCluster = (c: EpinioCluster) => {
  // Call '/ready' on each cluster. If there's a network error there's a good chance the user has to permit an invalid cert
  setClusterState(c, 'updating', {
    state: {
      transitioning: true,
      error:        false,
      message:       'Contacting...'
    }
  });

  store.dispatch(
    `epinio/request`,
    { opt: { url: EpinioInfoPath, redirectUnauthorized: false }, clusterId: c.id }
  )
  .then((res: any) => {
    c['version'] = res?.version;
    c['oidcEnabled'] = res?.oidc_enabled;
    setClusterState(
      c,
      'available',
      { state: { transitioning: false, error: false, message: "" } },
    );
  })
  .catch((e: Error) => {
    if (e.message === 'Network Error') {
      setClusterState(c, 'error', {
        state: {
          transitioning: false,
          error:   true,
          message: `Network Error. It may be that the certificate isn't trusted.
            Click on the URL above if you'd like to bypass checks and then refresh`
        }
      });
    } else {
      setClusterState(c, 'error', {
        state: {
          transitioning: false,
          error:   true,
          message: `Failed to check the ready state: ${ e }`
        }
      });
    }
  });
}

const columns: DataTableColumn[] = [
  {
    field: 'stateDisplay',
    label: 'State',
    width: '100px'
  },
  {
    field: 'name',
    label: 'Name'
  },
  {
    field: 'api',
    label: 'API'
  },
  {
    field: 'version',
    label: 'Version'
  },
  {
    field:    'actions',
    label:    'Actions',
    width:    '120px'
  }
];

return (_ctx: any,_cache: any) => {
  return (loading.value)
    ? (_openBlock(), _createBlock(Loading, {
        key: 0,
        mode: "main"
      }))
    : (allClusters.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createElementVNode("h2", null, _toDisplayString(_unref(t)('epinio.instances.none.header')), 1),
          _cache[0] || (_cache[0] = _createTextVNode()),
          _createElementVNode("p", null, _toDisplayString(_unref(t)('epinio.instances.none.description')), 1)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("h2", null, _toDisplayString(_unref(t)('epinio.instances.header')), 1),
              _cache[1] || (_cache[1] = _createTextVNode()),
              _createVNode(AsyncButton, {
                mode: "refresh",
                size: "sm",
                disabled: !canRediscover(),
                style: {"display":"inline-flex"},
                onClick: rediscover
              }, null, 8, ["disabled"])
            ]),
            _cache[5] || (_cache[5] = _createTextVNode()),
            _createVNode(DataTable, {
              rows: displayClusters.value,
              columns: columns,
              searchable: false
            }, {
              "cell:stateDisplay": _withCtx(({row}) => [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(BadgeStateFormatter, {
                    row: row,
                    value: row.stateDisplay
                  }, null, 8, ["row", "value"])
                ])
              ]),
              "cell:name": _withCtx(({row}) => [
                _createElementVNode("div", _hoisted_6, [
                  (row.state === 'available')
                    ? (_openBlock(), _createElementBlock("a", {
                        key: 0,
                        onClick: ($event: any) => (login(row))
                      }, _toDisplayString(row.name), 9, _hoisted_7))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createTextVNode(_toDisplayString(row.name), 1)
                      ], 64))
                ])
              ]),
              "cell:api": _withCtx(({row}) => [
                _createElementVNode("div", _hoisted_8, [
                  (row.state === 'uninstalled')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [], 64))
                    : (row.state !== 'available')
                      ? (_openBlock(), _createBlock(Link, {
                          key: 1,
                          row: row,
                          value: { text: row.api, url: row.infoUrl }
                        }, null, 8, ["row", "value"]))
                      : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                          _createTextVNode(_toDisplayString(row.api), 1)
                        ], 64))
                ])
              ]),
              "cell:actions": _withCtx(({row}) => [
                _createElementVNode("div", _hoisted_9, [
                  (row.state === 'uninstalled')
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "btn btn-sm role-primary",
                        onClick: ($event: any) => (openInstall(row))
                      }, "\n              Install Epinio\n            ", 8, _hoisted_10))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1,
              __: [2,3,4]
            }, 8, ["rows"])
          ])
        ]))
}
}

})